import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { RequireAuth } from '@/components/RequireAuth';
import { AppShell } from '@/components/AppShell';
import { Toaster } from 'sonner';

import LoginPage from '@/pages/LoginPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import InvitationAcceptPage from '@/pages/InvitationAcceptPage';
import DashboardPage from '@/pages/DashboardPage';
import LeadsPage from '@/pages/LeadsPage';
import LeadListsPage from '@/pages/LeadListsPage';
import LeadDetailPage from '@/pages/LeadDetailPage';
import PipelinePage from '@/pages/PipelinePage';
import TasksPage from '@/pages/TasksPage';
import InboxPage from '@/pages/InboxPage';
import WhatsAppTemplatesPage from '@/pages/WhatsAppTemplatesPage';
import QuickRepliesPage from '@/pages/QuickRepliesPage';
import CloseMacrosPage from '@/pages/CloseMacrosPage';
import CampaignsPage from '@/pages/CampaignsPage';
import ReportsPage from '@/pages/ReportsPage';
import EmailPage from '@/pages/EmailPage';
import EmailTemplatesPage from '@/pages/EmailTemplatesPage';
import CompaniesPage from '@/pages/CompaniesPage';
import CompanyDetailPage from '@/pages/CompanyDetailPage';
import DealsPage from '@/pages/DealsPage';
import AutomationsPage from '@/pages/AutomationsPage';
import FormsPage from '@/pages/FormsPage';
import WebchatPage from '@/pages/WebchatPage';
import CustomFieldsPage from '@/pages/CustomFieldsPage';
import UsersPage from '@/pages/UsersPage';
import TeamsPage from '@/pages/TeamsPage';
import AssignmentRulesPage from '@/pages/AssignmentRulesPage';
import ApiKeysPage from '@/pages/ApiKeysPage';
import AuditLogsPage from '@/pages/AuditLogsPage';
import SettingsPage from '@/pages/SettingsPage';

import SuperDashboardPage from '@/pages/super/SuperDashboardPage';
import SuperTenantsPage from '@/pages/super/SuperTenantsPage';
import SuperSmtpPage from '@/pages/super/SuperSmtpPage';
import SuperAuditLogsPage from '@/pages/super/SuperAuditLogsPage';

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === 'superadmin' ? '/superadmin/dashboard' : '/app/dashboard'} replace />;
}

function TenantLayout({ children }) { return <AppShell>{children}</AppShell>; }
function SuperLayout({ children }) { return <AppShell isSuper>{children}</AppShell>; }

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" theme="system" richColors closeButton toastOptions={{ className: 'font-sans', style: { borderRadius: 14 } }} />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite/:token" element={<InvitationAcceptPage />} />
          {/* Tenant routes */}
          <Route path="/app/dashboard" element={<RequireAuth role="tenant"><TenantLayout><DashboardPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/leads" element={<RequireAuth role="tenant"><TenantLayout><LeadsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/lead-lists" element={<RequireAuth role="tenant"><TenantLayout><LeadListsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/leads/:id" element={<RequireAuth role="tenant"><TenantLayout><LeadDetailPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/companies" element={<RequireAuth role="tenant"><TenantLayout><CompaniesPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/companies/:id" element={<RequireAuth role="tenant"><TenantLayout><CompanyDetailPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/deals" element={<RequireAuth role="tenant"><TenantLayout><DealsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/pipeline" element={<RequireAuth role="tenant"><TenantLayout><PipelinePage /></TenantLayout></RequireAuth>} />
          <Route path="/app/tasks" element={<RequireAuth role="tenant"><TenantLayout><TasksPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/inbox" element={<RequireAuth role="tenant"><TenantLayout><InboxPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/inbox/templates" element={<RequireAuth role="tenant"><TenantLayout><WhatsAppTemplatesPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/quick-replies" element={<RequireAuth role="tenant"><TenantLayout><QuickRepliesPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/close-macros" element={<RequireAuth role="tenant"><TenantLayout><CloseMacrosPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/campaigns" element={<RequireAuth role="tenant"><TenantLayout><CampaignsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/reports" element={<RequireAuth role="tenant"><TenantLayout><ReportsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/email" element={<RequireAuth role="tenant"><TenantLayout><EmailPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/email/templates" element={<RequireAuth role="tenant"><TenantLayout><EmailTemplatesPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/automations" element={<RequireAuth role="tenant"><TenantLayout><AutomationsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/forms" element={<RequireAuth role="tenant"><TenantLayout><FormsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/webchat" element={<RequireAuth role="tenant"><TenantLayout><WebchatPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/custom-fields" element={<RequireAuth role="tenant"><TenantLayout><CustomFieldsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/users" element={<RequireAuth role="tenant"><TenantLayout><UsersPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/teams" element={<RequireAuth role="tenant"><TenantLayout><TeamsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/assignment-rules" element={<RequireAuth role="tenant"><TenantLayout><AssignmentRulesPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/api-keys" element={<RequireAuth role="tenant"><TenantLayout><ApiKeysPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/audit-logs" element={<RequireAuth role="tenant"><TenantLayout><AuditLogsPage /></TenantLayout></RequireAuth>} />
          <Route path="/app/settings" element={<RequireAuth role="tenant"><TenantLayout><SettingsPage /></TenantLayout></RequireAuth>} />
          {/* SuperAdmin */}
          <Route path="/superadmin/dashboard" element={<RequireAuth role="super"><SuperLayout><SuperDashboardPage /></SuperLayout></RequireAuth>} />
          <Route path="/superadmin/tenants" element={<RequireAuth role="super"><SuperLayout><SuperTenantsPage /></SuperLayout></RequireAuth>} />
          <Route path="/superadmin/smtp" element={<RequireAuth role="super"><SuperLayout><SuperSmtpPage /></SuperLayout></RequireAuth>} />
          <Route path="/superadmin/audit-logs" element={<RequireAuth role="super"><SuperLayout><SuperAuditLogsPage /></SuperLayout></RequireAuth>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
